/**
 * useCropsReference — TanStack Query hook для справочника культур.
 *
 * Источник правды цвета и иконок культур — backend `reference.crops` (см.
 * spec NDVI-redesign-v1, миграция 021). Frontend кеширует результат навсегда
 * (staleTime: Infinity) — справочник меняется реже раза в сезон.
 *
 * Endpoint возвращает PagedResponse {total, items} — анврапаем в queryFn,
 * чтобы во всём UI работало `crops.data?.map(...)`. limit=500 — потолок API,
 * для пилота заведомо достаточно.
 *
 * Scope-aware режим (AK-61):
 *   useCropsReference({ inScope: { type: 'farm', id } }) → бьёт в
 *   /api/v1/reference/crops/in-scope?scope_type=farm&scope_id=... и возвращает
 *   только те культуры, что реально посеяны внутри scope. scope.type='company'
 *   (или отсутствие inScope) → fallback на полный справочник.
 *
 *   Кэш у scope-aware режима НЕ Infinity — посевы меняются (создание sowing'а
 *   в Field detail должно отразиться в Crops panel). staleTime=60s достаточно
 *   для UX «открыл /map → вижу актуальные культуры», без шквала запросов на
 *   каждом ре-рендере. Принудительный invalidate из CropFilterPanel при смене
 *   scope.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ActiveScope } from '@/lib/map/useMapStore';

export interface Crop {
  id: string;
  /** Production-endpoint возвращает name_translations + локальное name через CropListItem. */
  name: string;
  /** Hex color из reference.crops.color (миграция 021). Дефолт '#9CA3AF'. */
  color: string;
  /** URL иконки. Может быть null если иконка не загружена. */
  icon_url: string | null;
}

interface CropsListResponse {
  total: number;
  items: Array<{
    id: string;
    name?: string | null;
    code?: string | null;
    name_translations?: Record<string, string> | null;
    color?: string | null;
    icon_url?: string | null;
    [k: string]: unknown;
  }>;
}

function normalize(raw: CropsListResponse['items'][number]): Crop {
  // Имя: пробуем ru → name → code → fallback
  const name =
    (raw.name_translations && raw.name_translations.ru) ||
    raw.name ||
    raw.code ||
    'Без названия';
  return {
    id: raw.id,
    name,
    color: raw.color ?? '#9CA3AF',
    icon_url: raw.icon_url ?? null,
  };
}

export interface UseCropsReferenceOptions {
  /** Если задан и type !== 'company' — фильтруем культуры по scope (AK-61). */
  inScope?: ActiveScope;
}

export function useCropsReference(
  opts: UseCropsReferenceOptions = {},
): UseQueryResult<Crop[], Error> {
  const { inScope } = opts;
  const scoped =
    inScope && inScope.type !== 'company' && inScope.id
      ? { type: inScope.type, id: inScope.id }
      : null;

  return useQuery<Crop[], Error>({
    // Разные queryKey'и → независимые кэши для full-list и scoped-list.
    queryKey: scoped ? ['ref-crops', 'in-scope', scoped.type, scoped.id] : ['ref-crops'],
    queryFn: async () => {
      if (scoped) {
        const params = new URLSearchParams({
          scope_type: scoped.type,
          scope_id: scoped.id,
        });
        const resp = await api.get<CropsListResponse>(
          `/api/v1/reference/crops/in-scope?${params.toString()}`,
        );
        const items = Array.isArray(resp) ? resp : (resp?.items ?? []);
        return items.map(normalize);
      }
      const resp = await api.get<CropsListResponse>(
        '/api/v1/reference/crops?limit=500',
      );
      // Defensive: если backend вернёт массив (legacy) — поддерживаем
      const items = Array.isArray(resp) ? resp : (resp?.items ?? []);
      return items.map(normalize);
    },
    // Full-list — почти неизменяемый справочник (staleTime: Infinity).
    // Scoped — посевы могут меняться, держим короче, но достаточно чтобы не
    // фетчить на каждом ре-рендере панели.
    staleTime: scoped ? 60_000 : Infinity,
    gcTime: scoped ? 5 * 60_000 : Infinity,
  });
}
